import React, { useState } from 'react';
import { Button, Popup } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { Copy, Check } from 'lucide-react';

interface UrlWithCopyProps {
  url: string;
  displayText?: string;
  showCopyButton?: boolean;
  target?: string;
  maxLength?: number;
}

export const UrlWithCopy: React.FC<UrlWithCopyProps> = ({
  url,
  displayText,
  showCopyButton = true,
  target = '_blank',
  maxLength = 150
}) => {
  const [copied, setCopied] = useState(false);

  if (!url) {
    return null;
  }

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getDisplayText = () => {
    const text = displayText || url;
    if (maxLength && text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <a 
        href={url} 
        target={target} 
        rel="noopener noreferrer"
        style={{ 
          color: 'var(--gr-orange)', 
          textDecoration: 'none',
          wordBreak: 'break-all'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        {getDisplayText()}
      </a>
        {showCopyButton && (
        <Popup
          content={copied ? I18n.t('general.copied') : I18n.t('general.copy-link')}
          position="top center"
          open={copied ? true : undefined}
          trigger={
            <Button
              compact
              size="mini"
              icon
              onClick={handleCopy}
              style={{ 
                padding: '4px',
                minHeight: 'auto',
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }}
            >
              {copied ? (
                <Check 
                  size={18}
                  style={{ color: 'var(--gr-green)' }}
                />
              ) : (
                <Copy 
                  size={18}
                  style={{ color: 'var(--text-secondary)' }}
                />
              )}
            </Button>
          }
        />
      )}
    </div>
  );
};

export default UrlWithCopy;
